import { estimateFallbackTtsPlanningMs } from "@repo/topic-kernel/timeline";
import { isPlayableNarrationManifestStatus } from "./is-playable-narration-manifest-status";

export interface TopicCardClipManifestEntry {
	status?: string;
	/** Manifest v1 body clip length. */
	durationMs?: number;
	/** Manifest v2 explicit body clip length (falls back to `durationMs`). */
	bodyDurationMs?: number;
	/** Manifest v2 summary clip length. */
	summaryDurationMs?: number;
}

export interface ResolveTopicCardSegmentDurationInput {
	bodyText: string;
	audioEnabled?: boolean;
	segmentTrailingSilenceMs?: number;
	bodyManifest?: TopicCardClipManifestEntry | null;
	summaryManifest?: TopicCardClipManifestEntry | null;
	measuredBodyDurationMs?: number | null;
	measuredSummaryDurationMs?: number | null;
	cachedFallbackBodyDurationMs?: number | null;
	cachedFallbackSummaryDurationMs?: number | null;
}

export interface ResolveTopicCardSegmentDurationResult {
	bodyMs: number;
	summaryMs: number;
	/**
	 * Pause-gate metadata only (P8). Never included in {@link segmentMs} /
	 * PlaybackSession segment `durationMs`.
	 */
	trailingSilenceMs: number;
	/**
	 * Content duration = body + summary only.
	 * Does **not** include trailing silence.
	 */
	segmentMs: number;
}

function resolveReadyClipDurationMs(
	manifestEntry: TopicCardClipManifestEntry | null | undefined,
	explicitDurationMs: number | undefined,
	measuredDurationMs: number | null | undefined,
): number | null {
	if (manifestEntry == null) {
		return null;
	}
	if (!isPlayableNarrationManifestStatus(manifestEntry.status)) {
		return null;
	}
	const manifestDurationMs =
		typeof explicitDurationMs === "number" && explicitDurationMs > 0
			? explicitDurationMs
			: typeof manifestEntry.durationMs === "number" &&
					manifestEntry.durationMs > 0
				? manifestEntry.durationMs
				: null;
	if (manifestDurationMs == null) {
		return null;
	}
	if (typeof measuredDurationMs === "number" && measuredDurationMs > 0) {
		return measuredDurationMs;
	}
	return manifestDurationMs;
}

export function resolveTopicCardSegmentDurationMs(
	input: ResolveTopicCardSegmentDurationInput,
): ResolveTopicCardSegmentDurationResult {
	const audioEnabled = input.audioEnabled !== false;
	const trailingSilenceMs = Math.max(0, input.segmentTrailingSilenceMs ?? 0);
	const bodyText = input.bodyText.trim();

	let bodyMs = 0;
	if (bodyText.length > 0) {
		if (!audioEnabled) {
			bodyMs = estimateFallbackTtsPlanningMs(
				bodyText,
				input.cachedFallbackBodyDurationMs,
			);
		} else {
			const readyBodyMs = resolveReadyClipDurationMs(
				input.bodyManifest,
				input.bodyManifest?.bodyDurationMs ??
					input.bodyManifest?.durationMs,
				input.measuredBodyDurationMs,
			);
			bodyMs =
				readyBodyMs ??
				estimateFallbackTtsPlanningMs(
					bodyText,
					input.cachedFallbackBodyDurationMs,
				);
		}
	}

	const summaryMs = 0;

	return {
		bodyMs,
		summaryMs,
		trailingSilenceMs,
		// Content SoT for PlaybackSession / TopicDeck — silence is a pause gate (P8).
		segmentMs: bodyMs + summaryMs,
	};
}

export interface TopicSegmentDurationTopicOptions {
	audioEnabled?: boolean;
	segmentTrailingSilenceMs?: number;
}

export interface TopicSegmentDurationCardContext {
	narrationReadableText?: string | null;
	narrationEligible?: boolean;
	summaryText?: string | null;
}

export interface ResolveTopicCardSegmentDurationForCardInput {
	card: TopicSegmentDurationCardContext;
	bodyManifest?: TopicCardClipManifestEntry | null;
	summaryManifest?: TopicCardClipManifestEntry | null;
	topicOptions?: TopicSegmentDurationTopicOptions;
	measuredBodyDurationMs?: number | null;
	measuredSummaryDurationMs?: number | null;
	cachedFallbackBodyDurationMs?: number | null;
	cachedFallbackSummaryDurationMs?: number | null;
}

/** Returns `segmentMs` when the card is narration-eligible with speakable body text. */
export function resolveTopicCardSegmentDurationMsForNarrationCard(
	input: ResolveTopicCardSegmentDurationForCardInput,
): number | null {
	if (input.card.narrationEligible !== true) {
		return null;
	}
	const bodyText = input.card.narrationReadableText?.trim() ?? "";
	if (bodyText.length === 0) {
		return null;
	}
	return resolveTopicCardSegmentDurationMs({
		bodyText,
		audioEnabled: input.topicOptions?.audioEnabled,
		segmentTrailingSilenceMs: input.topicOptions?.segmentTrailingSilenceMs,
		bodyManifest: input.bodyManifest,
		summaryManifest: input.summaryManifest,
		measuredBodyDurationMs: input.measuredBodyDurationMs,
		measuredSummaryDurationMs: input.measuredSummaryDurationMs,
		cachedFallbackBodyDurationMs: input.cachedFallbackBodyDurationMs,
		cachedFallbackSummaryDurationMs: input.cachedFallbackSummaryDurationMs,
	}).segmentMs;
}

export interface TopicSegmentMeasuredDurations {
	bodyByCardId: Readonly<Record<string, number>>;
	summaryByCardId: Readonly<Record<string, number>>;
}

export function createEmptyTopicSegmentMeasuredDurations(): TopicSegmentMeasuredDurations {
	return { bodyByCardId: {}, summaryByCardId: {} };
}
